import type { ReactNode } from "react";
import { type ModalAction, VARIANT_BY_LEGACY_TYPE } from "src/components/Modal";
import { Button } from "src/components/ui/button";
import { BoldText, MediumText } from "src/components/v1/Text";
import { ChevronLeft } from "src/icons";
import { cn } from "src/utilities/shadcnUtils";

const STYLES = {
  container: "flex box-border flex-col justify-center items-center py-10 px-8 overflow-x-hidden",
  secondaryContainer: "justify-start items-start p-0",
  contentContainer: "p-8 pb-0 flex flex-col box-border w-full",
  textContainer: "mb-8",
  buttonRow:
    "w-full flex box-border flex-row justify-between items-center bg-muted px-8 py-4 mt-8 rounded-b-lg",
  header: "mt-6 text-lg text-foreground text-center leading-[160%]",
  secondaryHeader: "text-foreground text-lg",
  illustratedParagraph: "text-center text-sm text-muted-foreground leading-[160%]",
  illustratedBodyContainer: "mb-8 mt-2",
  inputParagraph: "text-sm text-muted-foreground leading-[160%] mt-4",
  button: "py-4 px-8",
  secondaryButton: "py-4 px-8 mt-1",
  secondaryButtonText: "text-green-500",
  chevron: "w-4 mr-1",
  backButton: "justify-start items-center pl-0",
  singleButton: "w-full",
  footerContainer: "mt-0 w-full",
  noActionsFooter: "mt-12",
  stackedFooter: "px-8 py-4 w-full flex flex-col items-center box-border",
  stackedPrimaryButton: "w-full",
  stackedSecondaryButton: "mt-2",
  stackedSpacer: "m-2",
};

export interface IllustratedModalContentProps {
  image: ReactNode;
  actions: ModalAction[];
  header: string;
  text?: string | string[] | ReactNode | ReactNode[];
  children?: ReactNode;
  className?: string;
}

export const IllustratedModalContent = ({
  header,
  text,
  image,
  actions,
  children,
  className,
}: IllustratedModalContentProps) => (
  <div className={cn(STYLES.container, className)}>
    {image}
    <BoldText className={STYLES.header}>{header}</BoldText>
    <div className={STYLES.illustratedBodyContainer}>
      {(Array.isArray(text) ? text : [text]).map((paragraph, index) =>
        typeof paragraph === "string" ? (
          <MediumText className={STYLES.illustratedParagraph} key={index}>
            {paragraph}
          </MediumText>
        ) : (
          paragraph
        )
      )}
      {children}
    </div>
    {actions.map((action, index) => (
      <Button
        key={`button-${index}`}
        className={index === 0 ? STYLES.button : cn(STYLES.secondaryButton, STYLES.secondaryButtonText)}
        variant={VARIANT_BY_LEGACY_TYPE[action.type ?? "primary"]}
        type="button"
        // @ts-expect-error
        onClick={action.action}
      >
        {action.text}
      </Button>
    ))}
  </div>
);

type onSubmit = (() => void) | ((event: React.ChangeEvent<HTMLFormElement>) => void);

export interface InputModalFormProps {
  header: string;
  children: ReactNode[] | ReactNode;
  footer?: ReactNode;
  paragraph?: string;
  onSubmit?: onSubmit;
  marginBottom?: number;
}

export const InputModalForm = ({
  children,
  header,
  paragraph,
  footer,
  onSubmit,
  marginBottom = 48,
}: InputModalFormProps) => (
  <form onSubmit={onSubmit}>
    <div
      className={cn(STYLES.container, STYLES.secondaryContainer)}
      style={{ marginBottom: footer ? 0 : marginBottom }}
    >
      <div className={STYLES.contentContainer}>
        <div className={STYLES.textContainer}>
          <BoldText className={STYLES.secondaryHeader}>{header}</BoldText>
          {paragraph ? <MediumText className={STYLES.inputParagraph}>{paragraph}</MediumText> : null}
        </div>
        {children}
      </div>
      {footer}
    </div>
  </form>
);

export interface InputModalFooterProps {
  nextDisabled?: boolean;
  actions?: ModalAction[];
  footerClassName?: string;
  isLoading?: boolean;
}

export const InputModalFooter = ({
  nextDisabled,
  actions,
  footerClassName,
  isLoading,
}: InputModalFooterProps) => {
  const secondaryButton = actions?.length ? (
    <Button
      className={STYLES.backButton}
      // @ts-expect-error
      onClick={actions[0].action}
      variant={VARIANT_BY_LEGACY_TYPE[actions[0].type ?? "ghost"]}
      type="button"
      iconPlacement="none"
    >
      <ChevronLeft className={STYLES.chevron} color="rgba(168, 162, 158, 1)" />
      <p>{actions[0].text}</p>
    </Button>
  ) : null;

  return (
    <div className={cn(STYLES.footerContainer, !actions && STYLES.noActionsFooter)}>
      {actions?.length ? (
        <div className={cn(STYLES.buttonRow, footerClassName)}>
          {actions[0].secondary
            ? secondaryButton
            : actions.length === 1 && (
                <Button
                  className={STYLES.singleButton}
                  variant={VARIANT_BY_LEGACY_TYPE[actions[0].type ?? "primary"]}
                  type={actions[0]?.submit ? "submit" : "button"}
                  disabled={nextDisabled}
                  // @ts-expect-error
                  onClick={actions[0].action}
                  pending={isLoading}
                >
                  {actions[0].text}
                </Button>
              )}
          {actions.length >= 2 && (
            <>
              {secondaryButton}
              <Button
                disabled={nextDisabled}
                // @ts-expect-error
                onClick={actions[1].action}
                variant={VARIANT_BY_LEGACY_TYPE[actions[1].type ?? "primary"]}
                type={actions[1]?.submit ? "submit" : "button"}
                pending={isLoading}
              >
                {actions[1].text}
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

// Vertically stacked full width buttons
export const StackedInputModalFooter = ({
  nextDisabled,
  actions,
  footerClassName,
  isLoading,
}: InputModalFooterProps) =>
  !actions?.length ? null : (
    <div className={cn(STYLES.stackedFooter, footerClassName)}>
      <Button
        className={STYLES.stackedPrimaryButton}
        variant={VARIANT_BY_LEGACY_TYPE[actions[0].type ?? "primary"]}
        type="button"
        // @ts-expect-error
        onClick={actions[0].action}
        pending={isLoading}
        disabled={nextDisabled}
      >
        {actions[0].text}
      </Button>
      {actions.length >= 2 && !!actions[1].action ? (
        <Button
          className={STYLES.stackedSecondaryButton}
          variant={VARIANT_BY_LEGACY_TYPE[actions[1].type ?? "ghost"]}
          type="button"
          // @ts-expect-error
          onClick={actions[1].action}
        >
          {actions[1].text}
        </Button>
      ) : (
        <div className={STYLES.stackedSpacer} />
      )}
    </div>
  );

export type InputModalContentProps = Omit<InputModalFormProps, "footer"> & InputModalFooterProps;

export const InputModalContent = ({
  nextDisabled,
  children,
  header,
  actions,
  paragraph,
  footerClassName,
  isLoading,
  onSubmit,
}: InputModalContentProps) => (
  <InputModalForm
    header={header}
    paragraph={paragraph}
    onSubmit={onSubmit}
    footer={
      <InputModalFooter
        nextDisabled={nextDisabled}
        actions={actions}
        footerClassName={footerClassName}
        isLoading={isLoading}
      />
    }
  >
    {children}
  </InputModalForm>
);
